import React, { type ReactNode } from 'react';
import {
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  View,
  type LayoutChangeEvent,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { colors, spacing, typography } from '../../../core/theme/tokens';

type ChartHScrollProps = {
  height: number;
  plotWidth: number;
  scrolls: boolean;
  onLayout: (event: LayoutChangeEvent) => void;
  children: ReactNode;
  /** Frozen y-axis labels (does not scroll). */
  axis?: ReactNode;
  axisWidth?: number;
  /** Renders under the plot and scrolls with it (legends, axis bars). */
  footer?: ReactNode;
  style?: StyleProp<ViewStyle>;
};

const HINT =
  Platform.OS === 'web'
    ? 'Scroll to see the full timeline'
    : 'Swipe to see the full timeline';

/**
 * Lets time-series charts keep a readable year spacing on small screens
 * instead of compressing every point into the card width.
 * The dollar axis stays pinned so values stay readable while swiping.
 */
export function ChartHScroll({
  height,
  plotWidth,
  scrolls,
  onLayout,
  children,
  axis,
  axisWidth = 0,
  footer,
  style,
}: ChartHScrollProps) {
  return (
    <View style={styles.wrap}>
      {scrolls ? <Text style={styles.hint}>{HINT}</Text> : null}
      <View
        style={[styles.frame, style, footer ? null : { height }]}
        onLayout={onLayout}
        accessibilityLabel={scrolls ? HINT : undefined}
      >
        <ScrollView
          horizontal
          nestedScrollEnabled
          directionalLockEnabled
          scrollEnabled={scrolls}
          bounces={scrolls}
          showsHorizontalScrollIndicator={scrolls}
          style={footer ? undefined : { height }}
          contentContainerStyle={footer ? undefined : { height }}
        >
          <View style={{ width: plotWidth }}>
            <View style={[styles.plot, { height }]}>{children}</View>
            {footer}
          </View>
        </ScrollView>
        {axis != null && axisWidth > 0 ? (
          <View
            pointerEvents="none"
            style={[styles.axisFreeze, { width: axisWidth, height }]}
          >
            {axis}
          </View>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    width: '100%',
    gap: spacing.xs,
  },
  hint: {
    ...typography.caption,
    color: colors.accent,
    fontSize: 11,
    fontWeight: '600',
  },
  frame: {
    width: '100%',
    overflow: 'hidden',
    position: 'relative',
  },
  plot: {
    position: 'relative',
  },
  axisFreeze: {
    position: 'absolute',
    left: 0,
    top: 0,
    zIndex: 4,
    backgroundColor: colors.surfaceElevated,
    borderRightWidth: StyleSheet.hairlineWidth,
    borderRightColor: colors.glassBorder,
  },
});
